import React, { useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import { ProgressTracker, type Stages } from '@atlaskit/progress-tracker';

import { messages } from '../../../messages';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { Step } from '../types';

import {
	CloseDrawerButtonWrapper,
	HeaderRightContentWrapper,
	HeaderWrapper,
	ProgressTrackerWrapper,
} from './styled';

interface OwnProps {
	testId: string;
	steps: any;
	currentStep: number;
	shouldShowHeader: boolean;
	handleDrawerOnClose: () => void;
	headerRightContent?: React.ReactNode;
}

export const Header = (props: OwnProps) => {
	const { testId, currentStep, steps, shouldShowHeader, handleDrawerOnClose, headerRightContent } =
		props;

	const { formatMessage } = useIntl();

	useEffect(() => {
		validateSteps();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Requirements:
	 * 1. Information steps can only occur outside of progress tracker steps
	 * 2. Information steps can occur at the beginning or at the end
	 * 3. There may be N number of information steps either at the beginning or the end
	 * 4. Steps can be composed of a mix of information and regular steps
	 * 5. Steps can only be information and no others
	 */

	const validateSteps = (): never | void => {
		steps.forEach((currentStep: Step, index: number, array: Step[]) => {
			const leftStep = array[index - 1];
			const rightStep = array[index + 1];

			if (leftStep === undefined || rightStep === undefined) {
				return;
			}

			if (
				currentStep.type === 'information' &&
				leftStep.type === 'progress' &&
				rightStep.type === 'progress'
			) {
				throw new Error(
					'Information steps must not exist between regular steps. Move it to the beginning or the end of the stack.',
				);
			}
		});
	};

	// Convert our steps to stages, which will be passed to the ProgressTracker component
	const parseSteps = (steps: any[]): Stages => {
		let preInfoStepsCount = 0;
		for (let i = 0; i < steps.length; i++) {
			if (steps[i].type === 'information') {
				preInfoStepsCount++;
			} else {
				break;
			}
		}

		const isAnyStepLoading = steps.some((step) => step.isFetchLoading || step.isSubmissionLoading);

		return steps
			.filter((step) => step.type === 'progress')
			.map((step, index) => {
				const actualStep = currentStep - preInfoStepsCount;
				return {
					id: step.id,
					label: step.label,
					noLink: !step.href && step.progressStepOnClick === undefined,
					href: step.href || '',
					onClick: step.progressStepOnClick,
					status:
						isAnyStepLoading && actualStep > index
							? 'disabled'
							: index === actualStep
								? 'current'
								: index < actualStep
									? 'visited'
									: 'unvisited',
					percentageComplete: actualStep > index ? 100 : 0,
				};
			});
	};

	return (
		<HeaderWrapper showDivider={shouldShowHeader}>
			<CloseDrawerButtonWrapper
				onClick={() => handleDrawerOnClose()}
				data-testid={`${testId}-close-drawer-button`}
			>
				<CrossIcon
					color="currentColor"
					LEGACY_size="medium"
					spacing="spacious"
					label={formatMessage(messages.closeDrawerButton)}
				></CrossIcon>
			</CloseDrawerButtonWrapper>
			{shouldShowHeader && (
				<ProgressTrackerWrapper>
					<ProgressTracker
						testId={`${testId}-progress-tracker`}
						items={parseSteps(steps)}
						label={formatMessage(messages.progressTracker)}
					/>
				</ProgressTrackerWrapper>
			)}
			{headerRightContent ? (
				<HeaderRightContentWrapper>{headerRightContent}</HeaderRightContentWrapper>
			) : null}
		</HeaderWrapper>
	);
};
